import { type ReactNode, type SVGProps, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Eye, EyeOff } from "lucide-react";

export const LockNoKeyholeIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <rect x="6" y="10" width="12" height="10" rx="2" ry="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" strokeLinecap="round" />
  </svg>
);

type AuthInputProps = {
  label: string;
  type?: "text" | "email" | "password";
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  leftIcon?: ReactNode;
  isValid: boolean;
  touched: boolean;
  setTouched: (value: boolean) => void;
  invalidText?: string;
  showValidation?: boolean;
  helperText?: string;
  inputTextColorClassName?: string;
  required?: boolean;
  showPasswordToggle?: boolean;
};

export default function AuthInput({
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  leftIcon,
  isValid,
  touched,
  setTouched,
  invalidText = "Invalid value",
  showValidation = true,
  helperText,
  inputTextColorClassName = "text-[#32445A]",
  required = false,
  showPasswordToggle = false,
}: AuthInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const showInvalid = showValidation && touched && !isValid;
  const inputType = showPasswordToggle && showPassword ? "text" : type;

  return (
    <div>
      <label className="mb-2 block text-[16px] font-normal text-[#50627A]">
        {label}
      </label>

      <div className={showInvalid ? "relative mb-2" : "relative mb-5"}>
        {leftIcon && (
          <span className="absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-[#9BA8BC]">
            {leftIcon}
          </span>
        )}

        <input
          type={inputType}
          placeholder={placeholder}
          value={value}
          required={required}
          onChange={(e) => {
            if (!touched) setTouched(true);
            onChange(e.target.value);
          }}
          onBlur={() => setTouched(true)}
          aria-invalid={showValidation && touched && !isValid}
          className={`h-[42px] w-full rounded-[10px] border border-[#c7d0dc] bg-white ${
            leftIcon ? "pl-12" : "pl-4"
          } ${
            showValidation || showPasswordToggle ? "pr-12" : "pr-4"
          } text-[15px] ${inputTextColorClassName} outline-none transition focus:border-[#c7d0dc] focus:ring-0`}
        />

        <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2">
          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="flex items-center text-[#9BA8BC] hover:text-[#6F7F96]"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" />
              ) : (
                <Eye className="h-[18px] w-[18px]" />
              )}
            </button>
          )}
          {showValidation &&
            touched &&
            (isValid ? (
              <CheckIcon className="text-[#22C55E]" sx={{ fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon
                className="text-[#E5484D]"
                sx={{ fontSize: 18 }}
              />
            ))}
        </div>
      </div>

      {showInvalid && (
        <div className="mb-5 text-[12px] font-normal text-[#E5484D]">
          {invalidText}
        </div>
      )}

      {helperText && (
        <p className="mb-5 text-[12px] font-normal text-[#6F7F96]">
          {helperText}
        </p>
      )}
    </div>
  );
}